import React, { useEffect, useState } from "react";
import { fmtIST } from "../lib/time";
import { api, formatApiErrorDetail } from "../lib/api";
import { fmtQty } from "../lib/format";
import useSubmitGuard from "../lib/useSubmitGuard";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { Plus, CheckCircle2, XCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import { Dialog, Input } from "./Suppliers";
import { useAuth } from "../context/AuthContext";
import DeleteRequestButton from "../components/DeleteRequestButton";

export default function InventoryAdjustments() {
  const { busy: submitBusy, guard: submitGuard, reset: resetGuard } = useSubmitGuard();
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [stock, setStock] = useState([]);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(null);
  const [decideOf, setDecideOf] = useState(null);
  const [items, setItems] = useState([{ material_id: "", system_qty: 0, actual_qty: 0, reason: "" }]);
  const [notes, setNotes] = useState("");
  // Wave D (Feb-2026) — GRN/Box-wise adjustment state.
  const [pickedMat, setPickedMat] = useState("");
  const [fifoGrns, setFifoGrns] = useState([]);
  const [selections, setSelections] = useState([]); // { ledger_id, material_id, grn_number, box_no, system_qty, actual_qty, reason }
  const { user } = useAuth();

  const onPickMaterial = async (mid) => {
    setPickedMat(mid); setFifoGrns([]);
    if (!mid) return;
    try {
      const r = await api.get("/inventory/fifo-by-grn", { params: { material_id: mid } });
      setFifoGrns(r.data || []);
    } catch (e) { /* ignore */ }
  };

  const getSel = (ledger_id) => selections.find((x) => x.ledger_id === ledger_id);

  const toggleSel = (box, grn, materialId, checked) => {
    if (checked) {
      setSelections((prev) => [...prev, {
        ledger_id: box.ledger_id,
        material_id: materialId,
        grn_number: grn.grn_number,
        box_no: box.box_no,
        system_qty: Number(box.qty_remaining),
        actual_qty: Number(box.qty_remaining),   // default = system, delta 0
        reason: "",
      }]);
    } else {
      setSelections((prev) => prev.filter((x) => x.ledger_id !== box.ledger_id));
    }
  };

  const updateSel = (ledger_id, patch) => {
    setSelections((prev) => prev.map((x) => x.ledger_id === ledger_id ? { ...x, ...patch } : x));
  };

  const selectionSummary = selections.length === 0
    ? "No boxes selected yet."
    : `${selections.length} box(es) selected. Total Δ: ${
        selections.reduce((a, x) => a + (Number(x.actual_qty) - Number(x.system_qty)), 0).toFixed(3).replace(/\.?0+$/, "")
      }`;

  const load = async () => {
    const [r, m, s] = await Promise.all([api.get("/inventory-adjustments"), api.get("/materials"), api.get("/inventory/stock-summary")]);
    setRows(r.data); setMaterials(m.data); setStock(s.data);
  };
  useEffect(() => { load(); }, []);

  const matCode = (id) => materials.find((m) => m.id === id)?.code || "";
  const matName = (id) => materials.find((m) => m.id === id)?.name || "—";
  const sysQty = (id) => stock.find((s) => s.material_id === id)?.qty ?? 0;

  const updateItem = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const addItem = () => setItems([...items, { material_id: "", system_qty: 0, actual_qty: 0, reason: "" }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const submit = async () => {
    // Wave D — validate & submit box-level selections
    if (selections.length === 0) { toast.error("Pick at least one box to adjust"); return; }
    if (selections.some((s) => !s.reason)) { toast.error("Reason is required for every selected box"); return; }
    const payload = {
      items: selections.map((s) => ({
        material_id: s.material_id,
        system_qty: Number(s.system_qty),
        actual_qty: Number(s.actual_qty),
        reason: s.reason,
        ledger_id: s.ledger_id,
        grn_number: s.grn_number,
        box_no: s.box_no,
      })).filter((it) => it.actual_qty !== it.system_qty),  // skip zero-delta rows
      notes,
    };
    if (payload.items.length === 0) { toast.error("All selected boxes have Δ = 0 — nothing to adjust"); return; }
    try {
      await submitGuard(async () => {
        await api.post("/inventory-adjustments", payload);
      });
      setOpen(false);
      toast.success(`Adjustment submitted — ${payload.items.length} box(es), pending Plant Head decision`);
      setPickedMat(""); setFifoGrns([]); setSelections([]); setNotes(""); resetGuard();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const decide = async (decision, role) => {
    try {
      const ep = role === "ph" ? `/inventory-adjustments/${decideOf.id}/ph-decision` : `/inventory-adjustments/${decideOf.id}/sa-decision`;
      await api.post(ep, { decision, remark: decideOf.remark || "" });
      toast.success(`Decision ${decision} recorded`);
      setDecideOf(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const isPH = user?.role?.name === "Plant Head/Admin" || user?.role?.is_super_admin;
  const isSA = user?.role?.is_super_admin;

  return (
    <div data-testid="adjustments-page">
      <PageHeader title="Manual Inventory Adjustment" subtitle="SU submits → Plant Head approves → Super Admin finalises"
        helpText="Use on reconciliation days when system qty differs from physical count. Positive deltas create an ADJUSTMENT lot; negative deltas consume FIFO. Full audit trail. SA approval is final."
        right={<button onClick={() => setOpen(true)} className="bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold" data-testid="new-adj-btn"><Plus size={14}/> New Adjustment</button>}
      />
      <div className="p-8">
        <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-4 py-2.5">Number</th>
                <th className="text-left px-4 py-2.5">Created At</th>
                <th className="text-left px-4 py-2.5">By</th>
                <th className="text-right px-4 py-2.5"># Items</th>
                <th className="text-left px-4 py-2.5">PH (at)</th>
                <th className="text-left px-4 py-2.5">SA (at)</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                  <td className="px-4 py-2.5 font-mono text-[#1565C0]">{r.number}</td>
                  <td className="px-4 py-2.5 text-[10px] font-mono text-[#0D2B5E]/70" data-testid={`adj-created-${r.number}`}>{fmtIST(r.created_at)}</td>
                  <td className="px-4 py-2.5 text-xs">{r.created_by_name}</td>
                  <td className="px-4 py-2.5 font-mono text-right">{r.items.length}</td>
                  <td className="px-4 py-2.5 text-xs">{r.ph_approved_by ? <>{r.ph_approved_by}<div className="text-[9px] text-[#0D2B5E]/50 font-mono">{fmtIST(r.ph_approved_at)}</div></> : "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{r.sa_approved_by ? <>{r.sa_approved_by}<div className="text-[9px] text-[#0D2B5E]/50 font-mono">{fmtIST(r.sa_approved_at)}</div></> : "—"}</td>
                  <td className="px-4 py-2.5">
                    <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: r.status === "approved" ? "#2E7D32" : r.status === "rejected" ? "#C62828" : "#E65100" }}>{r.status}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right space-x-1">
                    <button onClick={() => setView(r)} className="text-[#1565C0]" data-testid={`view-adj-${r.number}`}><Eye size={14}/></button>
                    {r.status === "pending_ph" && isPH && (
                      <button onClick={() => setDecideOf({ ...r, _role: "ph" })} className="text-xs bg-[#2E7D32] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold" data-testid={`ph-decide-${r.number}`}>PH Review</button>
                    )}
                    {r.status === "pending_sa" && isSA && (
                      <button onClick={() => setDecideOf({ ...r, _role: "sa" })} className="text-xs bg-[#1565C0] text-white px-2 py-1 rounded-sm uppercase tracking-wider font-bold" data-testid={`sa-decide-${r.number}`}>SA Review</button>
                    )}
                    <span className="inline-block align-middle"><DeleteRequestButton module="inventory_adjustment" record={r} label={r.number} onDeleted={load}/></span>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="p-10 text-center text-[#0D2B5E]/50">No adjustments yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <Dialog title="New Inventory Adjustment" onClose={() => setOpen(false)} wide>
          {/* Wave D (Feb-2026) — GRN/Box-wise adjustment.
              Workflow: pick material → expand GRNs → check specific boxes →
              enter actual qty per box. Falls back to pool-level (no ledger_id)
              only when no box is selected for that material. */}
          <div className="mb-3 px-3 py-2 bg-[#1565C0]/5 border-l-2 border-[#1565C0] text-[11px] text-[#0D2B5E]">
            Select a material, expand its GRNs, check the specific box(es) counted, and enter the Actual Qty for each. Stock is adjusted <b>per box</b> — overall / GRN / box inventory stays in sync.
          </div>
          <div className="mb-3">
            <label className="text-[10px] tracking-widest uppercase text-[#0D2B5E]/60 font-bold">Material</label>
            <select value={pickedMat} onChange={(e) => onPickMaterial(e.target.value)} data-testid="adj-pick-material"
              className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm mt-1">
              <option value="">Select material with in-stock inventory…</option>
              {stock.filter((s) => (s.qty || 0) > 0).map((s) => (
                <option key={s.material_id} value={s.material_id}>{s.code} — {s.name} (stock: {fmtQty(s.qty)})</option>
              ))}
            </select>
          </div>
          {pickedMat && (
            <div className="mb-4">
              <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-1">GRNs / Boxes</div>
              {(fifoGrns || []).length === 0 && <div className="text-xs text-[#0D2B5E]/50 p-3">No in-stock GRNs found.</div>}
              {(fifoGrns || []).map((g, gi) => {
                const grnKey = `${g.grn_number}-${g.lot_no}`;
                return (
                  <div key={grnKey} className="border border-[#EEEEEE] rounded-sm mb-2">
                    <div className="bg-[#F5F5F5] px-3 py-1.5 flex items-center justify-between text-xs">
                      <span className="font-mono font-bold text-[#0D2B5E]">{g.grn_number}</span>
                      <span className="text-[#0D2B5E]/60">Lot: {g.lot_no} · {g.boxes} box(es) · {fmtQty(g.qty)} in stock</span>
                    </div>
                    <table className="w-full text-xs">
                      <thead className="text-[9px] uppercase tracking-widest text-[#0D2B5E]/50">
                        <tr><th className="text-left px-2 py-1 w-8"></th><th className="text-left px-2 py-1">Box</th><th className="text-right px-2 py-1">System</th><th className="text-right px-2 py-1">Actual</th><th className="text-right px-2 py-1">Δ</th><th className="text-left px-2 py-1">Reason</th></tr>
                      </thead>
                      <tbody>
                        {(g.boxes_detail || []).map((b, bi) => {
                          const sel = getSel(b.ledger_id);
                          const checked = !!sel;
                          const delta = checked ? (Number(sel.actual_qty) - Number(b.qty_remaining)) : 0;
                          return (
                            <tr key={b.ledger_id} data-testid={`adj-box-${g.grn_number}-${b.box_no}`}>
                              <td className="p-1 text-center"><input type="checkbox" checked={checked} onChange={(e) => toggleSel(b, g, pickedMat, e.target.checked)} data-testid={`adj-box-chk-${g.grn_number}-${b.box_no}`}/></td>
                              <td className="p-1 font-mono">Box {b.box_no}</td>
                              <td className="p-1 font-mono text-right text-[#0D2B5E]/70">{fmtQty(b.qty_remaining)}</td>
                              <td className="p-1"><input type="number" step="any" min="0" disabled={!checked} value={sel?.actual_qty ?? ""} onChange={(e) => updateSel(b.ledger_id, { actual_qty: e.target.value })} data-testid={`adj-box-actual-${g.grn_number}-${b.box_no}`} className="w-20 border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm font-mono text-right disabled:bg-[#F5F5F5]"/></td>
                              <td className="p-1 font-mono text-right text-xs" style={{ color: !checked ? "#CCC" : delta === 0 ? "#0D2B5E" : delta > 0 ? "#2E7D32" : "#C62828" }}>{checked ? `${delta > 0 ? "+" : ""}${delta.toFixed(3).replace(/\.?0+$/, "")}` : "—"}</td>
                              <td className="p-1"><input value={sel?.reason || ""} disabled={!checked} onChange={(e) => updateSel(b.ledger_id, { reason: e.target.value })} placeholder="Damage, cycle count, etc." className="w-full border border-[#EEEEEE] px-2 py-1 text-xs rounded-sm disabled:bg-[#F5F5F5]"/></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
          <div className="text-xs text-[#0D2B5E]/60 mb-2">
            {selectionSummary}
          </div>
          <Input label="Notes (optional)" value={notes} onChange={setNotes}/>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={submit} disabled={submitBusy || selections.length === 0} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed" data-testid="save-adj-btn">{submitBusy ? "Submitting…" : "Submit"}</button>
          </div>
        </Dialog>
      )}

      {view && (
        <Dialog title={`Adjustment ${view.number}`} onClose={() => setView(null)} wide>
          <table className="w-full text-sm border border-[#EEEEEE]">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-2 py-1">Material</th>
                <th className="text-left px-2 py-1">GRN · Box</th>
                <th className="text-right px-2 py-1">System</th>
                <th className="text-right px-2 py-1">Actual</th>
                <th className="text-right px-2 py-1">Δ</th>
                <th className="text-left px-2 py-1">Reason</th>
              </tr>
            </thead>
            <tbody>
              {view.items.map((it, i) => (
                <tr key={i} className={i % 2 === 0 ? "" : "bg-[#F5F5F5]/40"}>
                  <td className="px-2 py-1">{matCode(it.material_id)} — {matName(it.material_id)}</td>
                  <td className="px-2 py-1 font-mono text-xs">
                    {it.grn_number ? `${it.grn_number}${it.box_no ? ` · Box ${it.box_no}` : ""}` : <span className="text-[#0D2B5E]/40">(pool)</span>}
                  </td>
                  <td className="px-2 py-1 font-mono text-right">{fmtQty(it.system_qty)}</td>
                  <td className="px-2 py-1 font-mono text-right">{fmtQty(it.actual_qty)}</td>
                  <td className="px-2 py-1 font-mono text-right" style={{ color: (it.actual_qty - it.system_qty) === 0 ? "#0D2B5E" : (it.actual_qty - it.system_qty) > 0 ? "#2E7D32" : "#C62828" }}>
                    {(it.actual_qty - it.system_qty) > 0 ? "+" : ""}{Number(it.actual_qty - it.system_qty).toFixed(3).replace(/\.?0+$/, "")}
                  </td>
                  <td className="px-2 py-1 text-xs">{it.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {view.notes && <div className="text-xs mt-3"><strong>Notes:</strong> {view.notes}</div>}
          {view.ph_remark && <div className="text-xs mt-2"><strong>PH:</strong> {view.ph_approved_by} — {view.ph_remark}</div>}
          {view.sa_remark && <div className="text-xs mt-1"><strong>SA:</strong> {view.sa_approved_by} — {view.sa_remark}</div>}
        </Dialog>
      )}

      {decideOf && (
        <Dialog title={`${decideOf._role.toUpperCase()} Decision — ${decideOf.number}`} onClose={() => setDecideOf(null)}>
          <textarea placeholder="Remark (optional)" value={decideOf.remark || ""} onChange={(e) => setDecideOf({ ...decideOf, remark: e.target.value })} rows={3} data-testid="adj-remark" className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm"/>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => decide("reject", decideOf._role)} className="px-4 py-2 bg-[#C62828] text-white text-sm rounded-sm font-semibold tracking-wider uppercase flex items-center gap-2" data-testid="adj-reject-btn"><XCircle size={14}/>Reject</button>
            <button onClick={() => decide("approve", decideOf._role)} className="px-4 py-2 bg-[#2E7D32] text-white text-sm rounded-sm font-semibold tracking-wider uppercase flex items-center gap-2" data-testid="adj-approve-btn"><CheckCircle2 size={14}/>Approve</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
